import {
  Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material'
import { CheckCircle, LockOutlined } from '@mui/icons-material'
import { BUNDLE_COPY } from './bundleCopy'

/**
 * How Enterprise is priced, as the two axes it actually has.
 *
 * Enterprise appeared as one card reading "Custom", beside Business and Free
 * cards carrying real figures. That told a reader almost nothing: not that the
 * price has two inputs, not what the bands are, and not that Exposure and
 * Evidence are separable.
 *
 * So: a matrix. Agent band down the side, bundle across the top.
 *
 * No money, on purpose
 * --------------------
 * The agent band and the bundles resolve into ONE contract, so there is no
 * per-cell figure to publish and no honest "from" either — a number in any
 * cell would be a quote nobody authorised, and the cheapest cell would become
 * the number every prospect anchors on. The matrix communicates the SHAPE of
 * the pricing, which is what a buyer needs before a call, and the call
 * supplies the figure.
 *
 * What the cells say instead
 * --------------------------
 * Core is included at every band; Exposure and Evidence are available at
 * every band. That is deliberately an unexciting grid, because the real
 * information is the AXES — and a cell that varied would imply a capability
 * gated by size, which is not how this is sold. The bundle columns carry what
 * each one answers, taken from the same copy the in-product teasers use, so a
 * prospect and a customer read identical words.
 */
const BANDS = [
  { label: '2K', agents: 'up to 2,000 agents' },
  { label: '5K', agents: 'up to 5,000 agents' },
  { label: '10K', agents: 'up to 10,000 agents' },
]

const COLUMNS = [
  {
    key: 'core',
    label: 'Core',
    included: true,
    answers:
      'What your estate is: agents, what they reach, what they run on, what '
      + 'they think with, and what they cost.',
  },
  {
    key: 'exposure',
    label: 'ARIAS Exposure',
    included: false,
    answers: BUNDLE_COPY.attack_paths.headline,
  },
  {
    key: 'evidence',
    label: 'ARIAS Evidence',
    included: false,
    answers: BUNDLE_COPY.certificates.headline,
  },
]

const EnterpriseMatrix = () => (
  <Box sx={{ mt: 6, maxWidth: 980, mx: 'auto' }}>
    <Box sx={{ textAlign: 'center', mb: 2 }}>
      <Typography variant="overline" color="text.secondary">Enterprise</Typography>
      <Typography variant="h5" gutterBottom>
        Two choices, one contract
      </Typography>
      <Typography variant="body2" color="text.secondary">
        How many agents you govern, and how deep you want to go. Core comes
        with every band; Exposure and Evidence are independent — either, both,
        or neither.
      </Typography>
    </Box>

    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Agent band</TableCell>
            {COLUMNS.map((c) => (
              <TableCell key={c.key} align="center" sx={{ fontWeight: 700 }}>
                {c.label}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', fontWeight: 400, mt: 0.5, maxWidth: 230, mx: 'auto' }}
                >
                  {c.answers}
                </Typography>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {BANDS.map((band) => (
            <TableRow key={band.label}>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {band.label}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {band.agents}
                </Typography>
              </TableCell>
              {COLUMNS.map((c) => (
                <TableCell key={c.key} align="center">
                  {c.included ? (
                    <Chip
                      size="small"
                      color="success"
                      variant="outlined"
                      icon={<CheckCircle sx={{ fontSize: 14 }} />}
                      label="Included"
                    />
                  ) : (
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<LockOutlined sx={{ fontSize: 13 }} />}
                      label="Add"
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Said plainly. An absence of prices that is not explained reads as an
        omission, or worse as something being hidden. */}
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}>
      The band and the bundles are priced together as one contract, with
      implementation, so there is no per-cell list price. Above 10,000 agents
      is a conversation rather than a band.
    </Typography>

    <Box sx={{ textAlign: 'center', mt: 2 }}>
      <Button
        variant="contained"
        href="mailto:sales@tryarias.com?subject=ARIAS%20Enterprise"
      >
        Talk to us
      </Button>
    </Box>
  </Box>
)

export default EnterpriseMatrix
